import type { ParticipantState } from "@exp/shared";
import { amountsFromShare } from "@exp/shared";
import { enqueue, startTrackingPass, syncOutboxPasses, type OutboxItem } from "./outbox";
import {
  bindEventSource,
  bindTrackingRetry,
  keepsAnswerOnStatus,
  queueEvent,
  scheduleFlush,
  notePhaseClock,
  deliverEvents,
  currentPhaseElapsed,
} from "./tracking/buffer";

const TOKEN_KEY = "exp_participant_token";
const memorySeq = new Map<string, number>();

export { queueEvent, scheduleFlush, notePhaseClock, currentPhaseElapsed };

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* storage blocked */
  }
}

export function storageBlocked(): boolean {
  try {
    localStorage.setItem("__t", "1");
    localStorage.removeItem("__t");
    return false;
  } catch {
    return true;
  }
}

export function resetEventSeqForTests(): void {
  memorySeq.clear();
}

export function nextSeq(
  token: string | null,
  storage?: Pick<Storage, "getItem" | "setItem"> | null,
): number {
  const key = `exp_event_seq:${token ?? ""}`;
  const memKey = token ?? "";
  const store = storage === undefined ? browserStorage() : storage;
  if (store) {
    try {
      const raw = Number(store.getItem(key) ?? "1");
      const n = Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
      store.setItem(key, String(n + 1));
      return n;
    } catch {
      /* storage blocked: fall through to the in-memory counter */
    }
  }
  const n = memorySeq.get(memKey) ?? 1;
  memorySeq.set(memKey, n + 1);
  return n;
}

function browserStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

bindEventSource((token) => nextSeq(token), () => getStoredToken());

export async function api<T>(
  path: string,
  opts: RequestInit & { token?: string | null } = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set("content-type", "application/json");
  const token = opts.token ?? getStoredToken();
  if (token) headers.set("x-participant-token", token);
  const res = await fetch(path, { ...opts, headers, credentials: "include" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error(err.error ?? "request_failed"), { status: res.status, body: err });
  }
  return res.json() as Promise<T>;
}

export async function joinByLink(link_token: string) {
  const r = await api<{ participant_token: string; public_code: string }>("/api/join/link", {
    method: "POST",
    body: JSON.stringify({
      link_token,
      device_class: detectDevice(),
      screen_w: window.screen.width,
      screen_h: window.screen.height,
      user_agent: navigator.userAgent.slice(0, 500),
      locale: navigator.language,
      app_build: "web",
    }),
  });
  storeToken(r.participant_token);
  return r;
}

export async function joinByCode(short_code: string) {
  const r = await api<{
    kind: "workshop" | "run";
    link_token?: string;
    participant_token?: string;
    public_code?: string;
  }>("/api/join/code", {
    method: "POST",
    body: JSON.stringify({
      short_code,
      device_class: detectDevice(),
      screen_w: window.screen.width,
      screen_h: window.screen.height,
      user_agent: navigator.userAgent.slice(0, 500),
      locale: navigator.language,
      app_build: "web",
    }),
  });
  if (r.kind === "run" && r.participant_token) storeToken(r.participant_token);
  return r;
}

export async function fetchState(token?: string | null): Promise<ParticipantState> {
  return api("/api/participant/state", { token });
}

function detectDevice(): "desktop" | "tablet" | "phone" | "unknown" {
  const w = window.innerWidth;
  if (w < 600) return "phone";
  if (w < 900) return "tablet";
  return "desktop";
}

async function postOutboxItem(item: OutboxItem): Promise<number | "network"> {
  try {
    const headers = new Headers({ "content-type": "application/json" });
    const token = getStoredToken();
    if (token) headers.set("x-participant-token", token);
    const res = await fetch(item.path, {
      method: item.method,
      headers,
      body: JSON.stringify(item.body),
      credentials: "include",
    });
    return res.status;
  } catch {
    return "network";
  }
}

async function sendTrackingItem(item: OutboxItem): Promise<"ok" | "stop"> {
  const { removeOutbox } = await import("./outbox");
  const { trackingOutboxItem } = await import("./tracking/buffer");
  const events = Array.isArray((item.body as { events?: unknown[] })?.events)
    ? ((item.body as { events: Parameters<typeof deliverEvents>[0] }).events)
    : [];
  const retry = await deliverEvents(events, async (chunk) => {
    const status = await postOutboxItem({ ...item, body: { events: chunk } });
    return { status };
  });
  if (retry.length === events.length && events.length > 0) return "stop";
  if (retry.length) await enqueue(trackingOutboxItem(crypto.randomUUID(), retry));
  await removeOutbox(item.id);
  return retry.length ? "stop" : "ok";
}

export async function syncOutbox(): Promise<void> {
  await syncOutboxPasses({
    sendAnswer: async (item) => {
      const status = await postOutboxItem(item);
      if (status === "network" || keepsAnswerOnStatus(status)) return false;
      return true;
    },
    sendTracking: sendTrackingItem,
  });
}

bindTrackingRetry(() => startTrackingPass(sendTrackingItem));

export { amountsFromShare };
